import React, { useState, useEffect } from 'react';
import { GA } from './glonetTheme';

const AnimatedBanner = ({ 
  messages, // Expect 'messages' to be an array of strings passed as a prop
  className = "", 
  minHeight = "2rem", 
  rounded = true,
  showToggleButton = false
}) => {
  const [text, setText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [messageIndex, setMessageIndex] = useState(0);
  const [isWaiting, setIsWaiting] = useState(false);
  const [viewMode, setViewMode] = useState('animated');

  // Use the provided messages prop. If it's undefined or empty, the banner will show nothing or an empty list.
  const messageList = messages || []; // Default to an empty array if no messages are passed

  useEffect(() => {
    if (viewMode !== 'animated' || !messageList || messageList.length === 0) { // Added check for empty messageList
        setText(''); // Clear text if no messages or not animated
        return;
    }
    const typeSpeed = 50;
    const deleteSpeed = 30;
    const waitTime = 2000;
    
    if (isWaiting) return;

    // Ensure messageIndex is always valid for the current messageList length
    const currentMessageIndex = messageIndex % messageList.length;
    const currentMessage = messageList[currentMessageIndex];

    if (isDeleting) {
      if (text === '') {
        setIsDeleting(false);
        // Advance to the next message, wrapping around the messageList length
        setMessageIndex((prev) => (prev + 1) % messageList.length);
        setIsWaiting(true);
        setTimeout(() => setIsWaiting(false), 500);
        return;
      }
      const timeout = setTimeout(() => setText(text.slice(0, -1)), deleteSpeed);
      return () => clearTimeout(timeout);
    }

    if (currentMessage && text === currentMessage) { // Added check for currentMessage existence
      setIsWaiting(true);
      setTimeout(() => {
        setIsWaiting(false);
        setIsDeleting(true);
      }, waitTime);
      return;
    }
    
    if (currentMessage) { // Only type if currentMessage exists
        const timeout = setTimeout(() => setText(currentMessage.slice(0, text.length + 1)), typeSpeed);
        return () => clearTimeout(timeout);
    }
  }, [text, isDeleting, messageIndex, isWaiting, messageList, viewMode]);

  const toggleViewMode = (e) => {
    // e.stopPropagation(); // Prevent event bubbling
    setViewMode((prevMode) => (prevMode === 'animated' ? 'list' : 'animated'));
  };

  const roundedClass = rounded ? "rounded-lg" : "";

  // If there are no messages to display, don't render the banner at all
  // or render a minimal version. For now, let's not render if messageList is empty.
  if (!messageList || messageList.length === 0) {
    return null; 
  }

  return (
    <div
      className={`py-3 px-4 shadow-md mb-3 ${roundedClass} ${className}`}
      style={{ background: GA.panel, borderTop: `1px solid ${GA.panelBorder}`, borderBottom: `1px solid ${GA.panelBorder}`, color: GA.text }}
    >
      <div className="max-w-4xl mx-auto flex items-start justify-between">
        <div className="flex-1">
          {viewMode === 'animated' ? (
            <p className="text-base sm:text-lg font-medium relative" style={{ minHeight }}>
              {text}
              {/* Only show cursor if there's text or it's about to type */}
              {(text || (!isWaiting && messageList.length > 0)) &&
                <span className={`inline-block w-0.5 h-5 ml-1 ${isWaiting ? 'opacity-0' : 'animate-pulse'}`} style={{ background: GA.accent }}></span>
              }
            </p>
          ) : (
            <ul className="text-sm sm:text-base font-medium space-y-1">
              {messageList.map((msg, index) => (
                <li key={index}>{msg}</li>
              ))}
            </ul>
          )}
        </div>
        {showToggleButton && (
          <button
            type="button" // Original comment
            onClick={toggleViewMode}
            className="text-gray-400 hover:text-white ml-4"
            title={viewMode === 'animated' ? 'Show as list' : 'Show animated'}
          >
            {viewMode === 'animated' ? '≡' : '▶'}
          </button>
        )}
      </div>
    </div>
  );
};

export default AnimatedBanner;